// src/components/Navigation/FeatureModals.tsx - Feature modals for Navbar
import React from 'react';
import FlashRTC from '../Features/FlashRTC';
import ExecutiveTraining from '../Features/ExecutiveTraining';
import ISO27001BCPSelector from '../Features/ISO27001BCPSelector';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

// Service content data
const serviceContent = {
  'FlashRTC': {
    title: 'Flash Real Time Communications (Flash RTC)',
    summary: 'Secure, out-of-band real-time communications through proprietary mobile app platform.',
    description: 'Flash AI provides secure, out-of-band real-time communications through its proprietary mobile app platform, Flash RTC. Built for Android and iOS mobile phones to allow connectivity during ransomware attacks, telecom failures, or other operational disruptions, Flash RTC ensures leadership teams and staff can continue to coordinate even when primary communication channels are compromised.',
    benefits: [
      'Maintain command and control during disruptions.',
      'Reduce downtime and confusion by keeping staff aligned.',
      'Gives insurers a resilient communications record. It is not a certification.',
      'Demonstrate reliability to clients, regulators, and stakeholders during crises.'
    ]
  },
  'Executive Training': {
    title: 'Flash Academy (LMS)',
    summary: 'Comprehensive online Learning Management System designed to train leaders, managers, and employees.',
    description: 'Flash AI offers a comprehensive online Learning Management System (LMS) designed to train leaders, managers, and employees in crisis readiness and response. Delivered through an easy-to-use digital platform, our LMS equips organizations with the skills and knowledge to anticipate threats, lead effectively during emergencies, and recover quickly.',
    benefits: [
      'Builds a culture of preparedness across the organization.',
      'Ensure leaders and staff have consistent, structured training aligned with ISO 22301:2019 and crisis management best practices.',
      'Reduces confusion and delays in real-world emergencies by ensuring everyone knows their role.',
      'Provides training records for regulators, insurers, and stakeholders. Records are not a certification.'
    ]
  },
  'ISO 27001 Disaster Recovery Plan': {
    title: 'ISO 27001 ITSM Disaster Recovery (DR) & Risk Identification Plan',
    summary: 'ISO 27001-aligned IT Service Management Disaster Recovery & Risk Identification Plan.',
    description: 'Flash AI provides an ISO 27001-aligned IT Service Management (ITSM) Disaster Recovery & Risk Identification Plan to help organizations understand, mitigate, and recover from today\'s evolving cybersecurity threats. This DR plan integrates threat identification, mitigation strategies, and disaster recovery techniques into a single, structured program.',
    benefits: [
      'Enhances cybersecurity resilience by preparing both systems and staff.',
      'Reduces the risk of costly downtime, data loss, and reputational damage.',
      'Maps information security controls to ISO/IEC 27001:2022. This does not certify the organization.',
      'Builds confidence among leadership, employees, and stakeholders that the organization is prepared to respond effectively to cyber incidents.'
    ]
  },
  'Tabletop Scenarios': {
    title: 'Tabletop Exercise Services',
    summary: 'Virtual tabletop exercises that simulate real-world crises such as cyberattacks or natural disasters.',
    description: 'Flash AI delivers virtual tabletop exercises guided by the RACI model (Responsible, Accountable, Consulted, Informed). Your team will practice roles, response actions, and communications in a safe environment, building confidence and showing resilience to insurers and stakeholders. This is not a certification.',
    benefits: [
      'Builds workforce confidence and readiness.',
      'Identifies gaps in response and communication before an actual incident occurs.',
      'Shows due diligence to insurers and stakeholders. It is not a certification.',
      'Reduces downtime, loss, and reputational damage when disruptions happen.'
    ]
  }
};

export interface FeatureModalsProps {
  // Feature modal states
  showFlashRTC: boolean;
  setShowFlashRTC: (show: boolean) => void;
  showExecutiveTraining: boolean;
  setShowExecutiveTraining: (show: boolean) => void;
  showISO27001: boolean;
  setShowISO27001: (show: boolean) => void;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
  upgradeFeature: { feature: 'essential' | 'gold' | 'premium', name: string } | null;
  setUpgradeFeature: (feature: { feature: 'essential' | 'gold' | 'premium', name: string } | null) => void;
}

const FeatureModals: React.FC<FeatureModalsProps> = ({
  showFlashRTC,
  setShowFlashRTC,
  showExecutiveTraining,
  setShowExecutiveTraining,
  showISO27001,
  setShowISO27001,
  showUpgradeModal,
  setShowUpgradeModal,
  upgradeFeature,
  setUpgradeFeature,
}) => {
  return (
    <>
      {/* FlashRTC Modal */}
      <FlashRTC
        isOpen={showFlashRTC}
        onClose={() => setShowFlashRTC(false)}
      />

      {/* Executive Training Modal */}
      <ExecutiveTraining
        isOpen={showExecutiveTraining}
        onClose={() => setShowExecutiveTraining(false)}
      />

      {/* ISO 27001 BCP Selector Modal */}
      <ISO27001BCPSelector
        isOpen={showISO27001}
        onClose={() => setShowISO27001(false)}
      />

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setUpgradeFeature(null);
        }}
        requiredFeature={upgradeFeature?.feature || 'essential'}
        featureName={upgradeFeature?.name || 'Feature'}
        selectedService={upgradeFeature?.name || null}
        serviceContent={upgradeFeature?.name ? serviceContent[upgradeFeature.name as keyof typeof serviceContent] : null}
      />
    </>
  );
};

export default FeatureModals;
